import Head from 'next/head'
import { useRouter } from 'next/router'
import { Page, Content, ButtonLink, Grid } from '@sh24/ui-components'
import SEO from '../components/SEO/seo'
import { CardModule, PersonSnippetModule, SearchInputModule } from '../components/Modules'
import InlineLink from '../components/InlineLink/inline-link'
import JSONSchema from '../components/JSONSchema/json-schema'
import documentToReactComponents from '../utils/document-to-react-components'
import { useAppContext } from '../contexts/app-context'
import { formatDate } from '../utils/helpers'

const getDateBlock = (prefix, date) => {
  const separator = prefix ? ': ' : ''
  return (
    <div key={prefix} className="body-sm mt-base">
      <i>
        {prefix}
        {separator}
        {date}
      </i>
    </div>
  )
}

const HelpPostTemplate = ({ page }) => {
  const { locale } = useRouter()
  const { appContext } = useAppContext()
  const { translations } = appContext

  const backLink = (
    <div key="return-to-help-centre" className="mb-md">
      <ButtonLink variation="text" animation="shiftLeft" iconPosition="left">
        <InlineLink
          external={false}
          text={translations['helpPost.backToHelpCentre']}
          url="/help-centre"
          iconPosition="left"
          iconName="arrow-left"
          context="content"
        />
      </ButtonLink>
    </div>
  )

  const headerContent = [backLink, <h1 key="help-post-title">{page.title}</h1>]

  const lastUpdated = formatDate({ dateString: page.lastUpdated, locale })
  const publishedAt = formatDate({ dateString: page.publishedAt, locale })

  const mainContent = [
    documentToReactComponents(page.content),
    /* eslint-disable jsx-a11y/aria-role */
    page.author ? (
      <div className="mt-lg mb-md">
        <PersonSnippetModule key="author" {...page.author} role="writer" />
      </div>
    ) : null,
    page.reviewer ? (
      <div className="mt-md mb-md">
        <PersonSnippetModule key="reviewer" {...page.reviewer} role="reviewer" />
      </div>
    ) : null,
    page.showPostDates && lastUpdated !== publishedAt
      ? getDateBlock(translations['page.lastUpdated'], lastUpdated)
      : null,
    page.showPostDates ? getDateBlock(translations['page.publishedAt'], publishedAt) : null,
    /* eslint-enable jsx-a11y/aria-role */
  ]

  return (
    <>
      {page.jsonSchema && <JSONSchema id={page.id} url={page.url} schema={page.jsonSchema} />}
      {page.seo && <SEO seo={page.seo} url={page.url} Head={Head} />}

      <Page.Section backgroundColour="/tertiary200" decoration="/curve/quarter/left">
        <Page.Content>
          <Content width="wide" textAlign="left" content={headerContent} />
        </Page.Content>
      </Page.Section>

      <Page.Section backgroundColour="/white">
        <Page.Content>
          <Content width="wide" textAlign="left" content={mainContent} />
        </Page.Content>
      </Page.Section>

      {page.searchInput && (
        <Page.Section backgroundColour="/white" decoration="/curve/quarter/leftInverse">
          <Page.Content>
            <SearchInputModule {...page.searchInput} />
          </Page.Content>
        </Page.Section>
      )}

      {page.recentPostList && (
        <Page.Section backgroundColour="/primary200">
          <Page.Content>
            <Content
              width="wide"
              textAlign="center"
              content={[
                <h2 key={page.recentPostList.title}>{page.recentPostList.title}</h2>,
                documentToReactComponents(page.recentPostList.introContent),
              ]}
            />

            <Grid width="wide" mobileColumns="1" tabletColumns="2" desktopColumns="2">
              {page.recentPostList.posts?.map((post) => (
                <CardModule
                  key={post.id}
                  id={post.id}
                  cardType="title"
                  backgroundColour="/primary200"
                  title={post.title}
                  link={post.url}
                  callToActionText={translations['recentPosts.callToAction']}
                />
              ))}
            </Grid>
          </Page.Content>
        </Page.Section>
      )}
    </>
  )
}

export default HelpPostTemplate
